import { useLayoutEffect, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { DialogContent, DialogActions } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useMembers } from '@app/api/members/useMembers';
import { fetchAllGroups } from '@app/store/slices/myTeamSlice';
import { ThemeButton } from '@shared/ui/buttons';
import { MemberTeams } from '@shared/ui/forms/display';
import {
  TelegramInput,
  EmailInput,
  BasicInput,
  AmountInput,
} from '@shared/ui/forms/inputs';
import { Status } from '@shared/ui/parts';
import { getUserRole, roles } from '@shared/static/roles';
import EditMemberResend from '@widgets/members/EditMemberResend';
import { MemberActionMenu } from '@widgets/tables/cells/members';
import {
  useFreezeMemberDialog,
  useChangeRoleDialog,
  useMoveMemberDialog,
  useDeleteMemberDialog,
  useUnfreezeMemberDialog,
} from '@utils/dialogs-hooks';
import { userNameRule, validateServerErrors } from '@utils/validation';
import { formatBalanceUi, FRACTION_DIGITS } from '@utils/balance-helpers';
import { hasActiveTeamLead } from '@utils/helpers';
import { theme } from '@utils/libs/theme';

export default function EditMemberDialog({
  close,
  data,
  setSubtitle,
  setTitle,
  setTitleAction,
  setHideCloseButton,
  setTitleIcon,
  setHint,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { openFreezeMemberDialog } = useFreezeMemberDialog();
  const { openUnfreezeMemberDialog } = useUnfreezeMemberDialog();
  const { openChangeRoleDialog } = useChangeRoleDialog();
  const { openMoveMemberDialog } = useMoveMemberDialog();
  const { openDeleteMemberDialog } = useDeleteMemberDialog();
  const { editMember } = useMembers();
  const { user } = useSelector((state) => state.user);
  const { allGroups } = useSelector((state) => state.my_team);
  const alreadyHasTeamLead = hasActiveTeamLead(data, allGroups);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { tariff } = useSelector((state) => state.tariff);

  const isLead = data.group_role === roles.TEAM_LEAD;
  const isTeamLeadSelf = isLead && data.id === user?.id;
  const isTeamLeadWithoutGroup = isLead && data.groups?.length === 0;

  useEffect(() => {
    dispatch(fetchAllGroups());
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors, dirtyFields, isSubmitting },
    setError,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: data.name,
      email: data.email,
      telegram: data.telegram,
      ...(!isTeamLeadSelf && {
        daily_card_issue_limit: data.daily_card_issue_limit,
        active_cards_limit: data.active_cards_limit,
        transaction_limit: data.transaction_limit,
        monthly_balance_limit: data.monthly_card_spent_limit,
      }),
      currentTeamMember: data.groups?.[0],
    },
  });

  useLayoutEffect(() => {
    if (data.isNew) {
      setTitle('member_invited');
      setSubtitle(t(getUserRole(data.group_role)));
      setTitleIcon('check-success');
      setHideCloseButton(true);
    } else {
      setTitle(data.name);
      setSubtitle(
        <div>
          <Status data={data} type="team" /> {t(getUserRole(data.group_role))}
        </div>,
      );
    }

    if (!user?.group_role) {
      if (!isMobile && !isTeamLeadWithoutGroup) {
        setTitleAction(
          <ThemeButton
            startIcon="profile"
            appearance="secondary"
            onClick={() => openChangeRoleDialog(data, alreadyHasTeamLead)}
          >
            {t('change_role')}
          </ThemeButton>,
        );
      }
      if (isMobile) {
        setTitleAction(<MemberActionMenu data={data} />);
      }
    }

    if (!data.groups?.length) {
      setHint(t('teamlead_without_group'));
    }
  }, [
    data,
    isTeamLeadWithoutGroup,
    isMobile,
    user?.group_role,
    t,
    alreadyHasTeamLead,
  ]);

  async function onSubmit(formData) {
    const limitFields = [
      'daily_card_issue_limit',
      'active_cards_limit',
      'transaction_limit',
      'monthly_balance_limit',
    ];
    const dirtyLimits = isTeamLeadSelf
      ? {}
      : Object.fromEntries(
          limitFields
            .filter((field) => dirtyFields[field])
            .map((field) => [field, Number(formData[field]) || null]),
        );

    const payload = {
      user_id: data.id,
      name: formData.name,
      telegram: formData.telegram,
      ...dirtyLimits,
    };

    try {
      const res = await editMember(payload);
      if (res) close();
    } catch (error) {
      validateServerErrors(setError, error);
    }
  }

  return (
    <>
      <DialogContent>
        {!data.is_activated && <EditMemberResend data={data} />}
        <div className="form">
          <div className="formGroup-grid">
            <BasicInput
              label={t('name')}
              error={errors.name}
              control={control}
              name="name"
              validation={userNameRule}
              placeholder={t('name_placeholder')}
              counter={true}
            />
            <div></div>
          </div>
          <div className="formGroup-grid">
            <EmailInput control={control} errors={errors} disabled />
            <TelegramInput errors={errors} control={control} />
          </div>
          {isLead ? (
            <MemberTeams data={data} />
          ) : (
            <div className="formGroup-grid">
              <BasicInput
                control={control}
                name="currentTeamMember"
                label={t('team')}
                disabled
                {...(!user?.group_role && {
                  onButtonClick: () => openMoveMemberDialog(data),
                  buttonIcon: 'refresh',
                })}
                isRequired={false}
              />
              <div></div>
            </div>
          )}
        </div>
        {!isTeamLeadSelf && (
          <>
            <div className="form-divider"></div>
            <div className="form">
              <div className="formGroup-grid">
                <AmountInput
                  integer
                  control={control}
                  placeholder={`${t('max')} ${tariff.daily_card_limit}`}
                  max={tariff.daily_card_limit}
                  name="daily_card_issue_limit"
                  label={t('daily_card_issue_limit')}
                  hint={`${t('max')} ${tariff.daily_card_limit}`}
                  tooltip={t('daily_card_issue_limit_tooltip')}
                  error={errors.daily_card_issue_limit}
                />
                <AmountInput
                  integer
                  control={control}
                  placeholder={`${t('max')} ${tariff.active_cards_limit}`}
                  max={tariff.active_cards_limit}
                  name="active_cards_limit"
                  label={t('active_cards_limit')}
                  hint={`${t('max')} ${formatBalanceUi(tariff.active_cards_limit, null, FRACTION_DIGITS.INTEGER)}`}
                  tooltip={t('active_cards_limit_tooltip')}
                  error={errors.active_cards_limit}
                />
              </div>
              <div className="formGroup-grid">
                <AmountInput
                  integer
                  control={control}
                  placeholder={`${t('max')} ${formatBalanceUi(tariff.transaction_limit, null, FRACTION_DIGITS.INTEGER)}`}
                  max={tariff.transaction_limit}
                  name="transaction_limit"
                  label={t('transaction_limit')}
                  hint={`${t('max')} ${formatBalanceUi(tariff.transaction_limit, 'USD', FRACTION_DIGITS.INTEGER)}`}
                  currency="USD"
                  tooltip={t('transaction_limit_tooltip')}
                  error={errors.transaction_limit}
                />
                <AmountInput
                  integer
                  control={control}
                  placeholder={`${t('max')} ${formatBalanceUi(tariff.monthly_card_spent_limit, null, FRACTION_DIGITS.INTEGER)}`}
                  max={tariff.monthly_card_spent_limit}
                  name="monthly_balance_limit"
                  label={t('card_limit')}
                  hint={`${t('max')} ${formatBalanceUi(tariff.monthly_card_spent_limit, 'USD', FRACTION_DIGITS.INTEGER)}`}
                  currency="USD"
                  tooltip={t('card_limit_tooltip')}
                  error={errors.monthly_balance_limit}
                />
              </div>
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {isMobile ? (
          <div className={data.isNew ? 'btns-toolbar' : 'w-full'}>
            <ThemeButton
              startIcon="check"
              onClick={handleSubmit(onSubmit)}
              loading={isSubmitting}
            >
              {t('save')}
            </ThemeButton>
            {data.isNew && (
              <ThemeButton appearance="secondary" onClick={close}>
                {t('skip_fill_later')}
              </ThemeButton>
            )}
          </div>
        ) : (
          <div className="btns-toolbar-three">
            <div className="d-flex">
              <ThemeButton
                block={false}
                minWidth
                startIcon="check"
                onClick={handleSubmit(onSubmit)}
                loading={isSubmitting}
              >
                {t('save')}
              </ThemeButton>
              {data.isNew && (
                <ThemeButton
                  block={false}
                  appearance="secondary"
                  onClick={close}
                >
                  {t('skip_fill_later')}
                </ThemeButton>
              )}
            </div>
            <div className="d-flex">
              {data.is_frozen ? (
                <ThemeButton
                  block={false}
                  appearance="secondary"
                  onClick={() => openUnfreezeMemberDialog(data)}
                  startIcon="unfreeze"
                >
                  {t('unfreeze')}
                </ThemeButton>
              ) : (
                <ThemeButton
                  block={false}
                  appearance="freeze"
                  onClick={() => openFreezeMemberDialog(data)}
                  startIcon="freeze"
                >
                  {t('freeze')}
                </ThemeButton>
              )}
              {!user?.group_role && (
                <ThemeButton
                  block={false}
                  appearance="delete"
                  startIcon="delete"
                  onClick={() => openDeleteMemberDialog(data)}
                >
                  {t('delete')}
                </ThemeButton>
              )}
            </div>
          </div>
        )}
      </DialogActions>
    </>
  );
}
